import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuthStore } from '../store/authStore';
import { useCms } from '../contexts/CmsContext';
import { useCmsIntegration } from '../hooks/useCmsIntegration';
import { LoginThemeProvider, useLoginTheme } from '../contexts/LoginThemeContext';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import XuiMigrationModal from '../components/modals/XuiMigrationModal';
import '../styles/theme.css';

const PersonalizedLoginForm: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { login, isAuthenticated, user, logout } = useAuthStore();
  const { currentCms, loading, error } = useCms();
  const { theme, isLoading: themeLoading } = useLoginTheme();
  
  // Integrar dados do CMS com ColorContext
  useCmsIntegration();
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userInfo, setUserInfo] = useState<any>(null);

  // O CmsContext já gerencia o carregamento dos dados

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Em mobile pode existir sessão "quebrada" (cache sem member_group_id/panel), que leva a loop de 403.
      // Se faltar hierarquia básica, forçar limpeza e permitir login novamente.
      const mg = Number((user as any)?.member_group_id || 0);
      const hasBasic = [1, 2, 3, 4, 6].includes(mg);
      if (!hasBasic) {
        try {
          logout(slug ? `/${slug}/login` : '/');
        } catch (_) {
          try {
            localStorage.removeItem('auth-storage');
            localStorage.removeItem('_pv001');
          } catch (_) {}
        }
        return;
      }

      // Redirecionar somente se houver slug válido
      if (slug && slug.trim() !== '') navigate(`/${slug}/dashboard`);
    }
  }, [isAuthenticated, navigate, slug, user, logout]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setLoginError('Por favor, preencha todos os campos');
      return;
    }

    setLoginLoading(true);
    setLoginError(null);

    try {
      await login({ username: formData.username, password: formData.password });
      // Redirecionar para a rota com slug após login, apenas se presente
      if (slug && slug.trim() !== '') {
        navigate(`/${slug}/dashboard`);
      } else {
        setLoginError('Slug ausente na URL. Acesse pelo link correto do seu CMS.');
      }
    } catch (err: any) {
      console.error('Erro no login:', err);
      // Verificar se é erro 403 com needsMigration
      if (err.response?.status === 403 && err.response?.data?.needsMigration) {
        const userInfoData = err.response.data.userInfo;
        setUserInfo(userInfoData);
        setUserEmail(''); // Não definir email automaticamente
        setShowMigrationModal(true);
        setLoginError('Usuário encontrado no sistema IPTV. Complete a migração para acessar o CMS.');
      } else {
        // Tratar diferentes tipos de erro
        if (err.response?.status === 403 && err.response?.data?.needsMigration) {
          setLoginError('Usuário encontrado no sistema IPTV. Complete a migração para acessar o CMS.');
        } else if (err.response?.status === 400) {
          setLoginError('Usuário não encontrado ou dados inválidos.');
        } else if (err.response?.status === 401) {
          setLoginError('Senha incorreta. Verifique suas credenciais.');
        } else {
          setLoginError('Erro de conexão. Verifique sua internet e tente novamente.');
        }
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleMigrationSuccess = (user: any) => {
    setShowMigrationModal(false);
    // Redirecionar para a rota com slug após migração
    if (slug && slug.trim() !== '') {
      navigate(`/${slug}/dashboard`);
    }
  };

  if (loading || themeLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${theme?.colors.background || '#F8FAFC'} 0%, ${theme?.colors.primary || '#3B82F6'}20 100%)` 
        }}
      >
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: theme?.colors.primary || '#3B82F6' }} />
          <p className="text-lg font-medium" style={{ color: theme?.colors.text || '#1F2937' }}>
            Carregando {theme?.cmsName || currentCms?.cmsName || 'CMS'}...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${theme?.colors.background || '#F8FAFC'} 0%, ${theme?.colors.primary || '#3B82F6'}20 100%)` 
        }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Erro</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/login')} 
              className="w-full"
              style={{ backgroundColor: currentCms?.colors.primary || '#3B82F6' }}
            >
              Voltar ao Login Padrão
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: `linear-gradient(135deg, ${theme?.colors.background || '#F8FAFC'} 0%, ${theme?.colors.primary || '#3B82F6'}20 100%)` 
      }}
    >
      <Card className="w-full max-w-md shadow-2xl mx-4">
        <CardHeader className="text-center space-y-4 p-6">
          {/* Logo do CMS */}
          {theme?.logoUrl || currentCms?.logoUrl ? (
            <div className="flex justify-center">
              <img 
                src={theme?.logoUrl || currentCms?.logoUrl} 
                alt={theme?.cmsName || currentCms?.cmsName}
                className="h-12 sm:h-16 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'block';
                  }
                }}
              />
              <div className="hidden text-xl sm:text-2xl font-bold" style={{ color: theme?.colors.primary || currentCms?.colors.primary }}>
                {theme?.cmsName || currentCms?.cmsName}
              </div>
            </div>
          ) : (
            <div 
              className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-white text-xl sm:text-2xl font-bold"
              style={{ backgroundColor: theme?.colors.primary || currentCms?.colors.primary || '#3B82F6' }}
            >
              {(theme?.cmsName || currentCms?.cmsName)?.charAt(0) || 'C'}
            </div>
          )}
          
          <div>
            <CardTitle className="text-xl sm:text-2xl" style={{ color: '#1F2937' }}>
              {theme?.cmsName || currentCms?.cmsName || 'CMS'}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Acesse sua conta para continuar
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Nome de usuário</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Digite seu nome de usuário"
                className="w-full"
                style={{ 
                  color: '#1F2937',
                  borderColor: (currentCms?.colors.primary || '#3B82F6') + '40'
                }}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Digite sua senha"
                  className="w-full pr-10"
                  style={{ 
                    color: '#1F2937',
                    borderColor: (currentCms?.colors.primary || '#3B82F6') + '40'
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {loginError && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{loginError}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loginLoading}
              style={{ 
                backgroundColor: theme?.colors.primary || currentCms?.colors.primary || '#3B82F6',
                borderColor: theme?.colors.primary || currentCms?.colors.primary || '#3B82F6',
                color: 'white'
              }}
            >
              {loginLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Modal de Migração XUI */}
      <XuiMigrationModal
        isOpen={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        onSuccess={handleMigrationSuccess}
        username={formData.username}
        email={userEmail}
        userInfo={userInfo}
      />
    </div>
  );
};

const PersonalizedLogin: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  return (
    <LoginThemeProvider slug={slug}>
      <PersonalizedLoginForm />
    </LoginThemeProvider>
  );
};

export default PersonalizedLogin;
